import React, { useMemo, useState, useRef, useEffect } from 'react';
import { X, Pencil } from 'lucide-react';
import { toast } from 'react-toastify';
import MultiSelectDropdownInline from '../multi-select-inline';
import SelectDropdown from '../select';
import MechanicalContractorDialogs from '../mechanical-contractor-management/MechanicalContractorDialogs';
import { MechanicalContractorFormData, MechanicalContractorFormErrors, initialMechanicalContractorFormData, initialMechanicalContractorFormErrors } from '../mechanical-contractor-management/dialogTypes';
import { validateStep1, validateStep2 } from '../mechanical-contractor-management/validation';
import { ContractorService } from '../../services/contractor.service';
import { lookupService, Province } from '../../services/lookups.service';
import { getCoordinatesFromAddress } from '../contractor-management/utils';
import { ContractorLocalTradeSelection, ContractorTradeEntry } from '../../types/jobsite-visits.types';

interface ContractorData {
  id: string;
  name: string;
  contractorTrades?: Array<{
    id: string;
    localUnion?: {
      id: string;
      code: string;
      unionNumber: number;
    };
    trade?: {
      id: string;
      name: string;
    };
  }>;
  address?: Array<{
    province?: { id: string; name: string } | string;
  }>;
}

interface Props {
  contractors: ContractorData[];
  selectedContractors: ContractorLocalTradeSelection[];
  onChange: (contractors: ContractorLocalTradeSelection[]) => void;
  onContractorUpdate: (contractor: ContractorData) => void;
  errors?: { [key: string]: string };
}

const MultiContractorTradesStep: React.FC<Props> = ({
  contractors,
  selectedContractors,
  onChange,
  onContractorUpdate,
  errors = {}
}) => {
  // State for managing contractor add/edit modal
  const [showContractorModal, setShowContractorModal] = useState(false);
  const [editingContractorId, setEditingContractorId] = useState<string | null>(null);

  // Modal dialog states
  const [showSaveConfirmDialog, setShowSaveConfirmDialog] = useState(false);
  const [showCloseConfirmDialog, setShowCloseConfirmDialog] = useState(false);
  const [pendingCloseAction, setPendingCloseAction] = useState<null | (() => void)>(null);

  // Form states
  const [currentModalStep, setCurrentModalStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<MechanicalContractorFormData>(initialMechanicalContractorFormData);
  const [formErrors, setFormErrors] = useState<MechanicalContractorFormErrors>(initialMechanicalContractorFormErrors);
  const [hasShownSimilarNameWarning, setHasShownSimilarNameWarning] = useState(false);

  // Province options
  const [provinceOptions, setProvinceOptions] = useState<Province[]>([]);
  const [isLoadingProvinces, setIsLoadingProvinces] = useState(false);

  // Refs
  const addMechanicalContractorDialogContentRef = useRef<HTMLDivElement>(null);

  // Load provinces on mount
  useEffect(() => {
    const loadProvinces = async () => {
      setIsLoadingProvinces(true);
      try {
        const provinces = await lookupService.getProvinces();
        setProvinceOptions(provinces);
      } catch (error) {
        console.error('Error loading provinces:', error);
      } finally {
        setIsLoadingProvinces(false);
      }
    };
    loadProvinces();
  }, []);
  // Get available contractors for multi-select (only those with trades and locals)
  const contractorOptions = useMemo(() => {
    return contractors
      .filter(c => {
        // Only include contractors that have contractorTrades with both localUnion and trade
        return c.contractorTrades &&
          c.contractorTrades.length > 0 &&
          c.contractorTrades.some(ct => ct.localUnion && ct.trade);
      })
      .map(c => {
        const province = c.address?.[0]?.province;
        const provinceName = typeof province === 'object' && province !== null
          ? province.name
          : province;
        return {
          value: c.id,
          label: provinceName ? `${c.name} (${provinceName})` : c.name
        };
      });
  }, [contractors]);

  // Get selected contractor IDs for multi-select
  const selectedContractorIds = useMemo(() => {
    return selectedContractors.map(sc => sc.contractorId);
  }, [selectedContractors]);

  // Handle contractor selection change
  const handleContractorSelectionChange = (selectedIds: string[]) => {
    const newContractors: ContractorLocalTradeSelection[] = selectedIds.map(contractorId => {
      const existing = selectedContractors.find(sc => sc.contractorId === contractorId);
      if (existing) {
        return existing;
      }
      const contractor = contractors.find(c => c.id === contractorId);

      // Locals and trades are chosen manually by the user via handleLocalSelectionChange /
      // handleTradeSelectionChange — a newly selected contractor starts with none selected.
      return {
        contractorId,
        contractorName: contractor?.name || '',
        locals: []
      };
    });
    onChange(newContractors);
  };

  // Get available locals for a specific contractor
  const getAvailableLocals = (contractorId: string) => {
    const contractor = contractors.find(c => c.id === contractorId);
    if (!contractor?.contractorTrades) return [];

    const uniqueLocalsMap = new Map();
    contractor.contractorTrades
      .filter(trade => trade.localUnion)
      .forEach(trade => {
        const local = trade.localUnion!;
        uniqueLocalsMap.set(local.id, {
          value: local.id,
          label: `Local ${local.unionNumber}`
        });
      });

    return Array.from(uniqueLocalsMap.values());
  };

  // Get available trades for a specific contractor and local
  const getAvailableTrades = (contractorId: string, localUnionId: string) => {
    const contractor = contractors.find(c => c.id === contractorId);
    if (!contractor?.contractorTrades) return [];

    return contractor.contractorTrades
      .filter(trade => trade.localUnion?.id === localUnionId && trade.trade)
      .map(trade => ({
        value: trade.trade!.id,
        label: trade.trade!.name
      }));
  };

  // Handle local selection for a contractor
  const handleLocalSelectionChange = (contractorId: string, selectedLocalIds: string[]) => {
    const updatedContractors = selectedContractors.map(contractor => {
      if (contractor.contractorId !== contractorId) return contractor;

      const newLocals = selectedLocalIds.map(localId => {
        const existing = contractor.locals.find(l => l.localUnionId === localId);
        if (existing) return existing;

        const localOption = getAvailableLocals(contractorId).find(l => l.value === localId);
        return {
          localUnionId: localId,
          localUnionName: localOption?.label || '',
          trades: []
        };
      });

      return {
        ...contractor,
        locals: newLocals
      };
    });
    onChange(updatedContractors);
  };

  // Handle trade selection for a contractor-local combination
  const handleTradeSelectionChange = (contractorId: string, localUnionId: string, selectedTradeIds: string[]) => {
    const updatedContractors = selectedContractors.map(contractor => {
      if (contractor.contractorId !== contractorId) return contractor;

      const updatedLocals = contractor.locals.map(local => {
        if (local.localUnionId !== localUnionId) return local;

        const newTrades: ContractorTradeEntry[] = selectedTradeIds.map(tradeId => {
          const existing = local.trades.find(t => t.tradeId === tradeId);
          if (existing) return existing;

          const tradeOption = getAvailableTrades(contractorId, localUnionId).find(t => t.value === tradeId);
          return {
            tradeId,
            tradeName: tradeOption?.label || '',
            unionType: 'union',
            journeymen: 0,
            apprentices: 0
          };
        });

        return {
          ...local,
          trades: newTrades
        };
      });

      return {
        ...contractor,
        locals: updatedLocals
      };
    });
    onChange(updatedContractors);
  };

  // Handle trade field changes (union type, journeymen, apprentices)
  const handleTradeFieldChange = (
    contractorId: string,
    localUnionId: string,
    tradeId: string,
    field: 'unionType' | 'journeymen' | 'apprentices',
    value: any
  ) => {
    const updatedContractors = selectedContractors.map(contractor => {
      if (contractor.contractorId !== contractorId) return contractor;

      const updatedLocals = contractor.locals.map(local => {
        if (local.localUnionId !== localUnionId) return local;

        const updatedTrades = local.trades.map(trade => {
          if (trade.tradeId !== tradeId) return trade;

          return {
            ...trade,
            [field]: field === 'unionType' ? value : parseInt(value) || 0
          };
        });

        return {
          ...local,
          trades: updatedTrades
        };
      });

      return {
        ...contractor,
        locals: updatedLocals
      };
    });
    onChange(updatedContractors);
  };

  // Remove contractor
  const handleRemoveContractor = (contractorId: string) => {
    const updatedContractors = selectedContractors.filter(c => c.contractorId !== contractorId);
    onChange(updatedContractors);
  };

  // Remove local from contractor
  const handleRemoveLocal = (contractorId: string, localUnionId: string) => {
    const updatedContractors = selectedContractors.map(contractor => {
      if (contractor.contractorId !== contractorId) return contractor;

      return {
        ...contractor,
        locals: contractor.locals.filter(l => l.localUnionId !== localUnionId)
      };
    });
    onChange(updatedContractors);
  };

  // Remove trade from local
  const handleRemoveTrade = (contractorId: string, localUnionId: string, tradeId: string) => {
    const updatedContractors = selectedContractors.map(contractor => {
      if (contractor.contractorId !== contractorId) return contractor;

      const updatedLocals = contractor.locals.map(local => {
        if (local.localUnionId !== localUnionId) return local;

        return {
          ...local,
          trades: local.trades.filter(t => t.tradeId !== tradeId)
        };
      });

      return {
        ...contractor,
        locals: updatedLocals
      };
    });
    onChange(updatedContractors);
  };

  // Function to reset form data to initial state
  const resetFormData = () => {
    setFormData(initialMechanicalContractorFormData);
    setCurrentModalStep(1);
    setFormErrors(initialMechanicalContractorFormErrors);
    setHasShownSimilarNameWarning(false);
  };

  // Handle opening modal for adding new contractor
  const handleAddContractor = () => {
    resetFormData();
    setEditingContractorId(null);
    setShowContractorModal(true);
  };

  // Handle opening modal for editing existing contractor
  const handleEditContractor = async (contractorId: string) => {
    try {
      // Fetch the full contractor data
      const contractorResponse: any = await ContractorService.getContractorById(contractorId);
      const contractorData = contractorResponse.data;
      // Transform contractor data to form data
      const headOffice = contractorData.address?.find((addr: any) => addr.addressType === 'head-office');
      const subOffices = contractorData.address?.filter((addr: any) => addr.addressType === 'sub-office') || [];

      // Transform contractorTrades to localTradeSelections
      const tradesByLocal = new Map<string, { localUnionName: string, tradeIds: string[], unionType: string }>();

      contractorData.contractorTrades?.forEach((ct: any) => {
        const localId = ct.localUnion?.id;
        if (!localId) return;

        if (!tradesByLocal.has(localId)) {
          tradesByLocal.set(localId, {
            localUnionName: `Local ${ct.localUnion.unionNumber}`,
            tradeIds: [],
            unionType: ct.unionType || 'union'
          });
        }

        if (ct.trade?.id) {
          tradesByLocal.get(localId)!.tradeIds.push(ct.trade.id);
        }
      });

      const localTradeSelections = Array.from(tradesByLocal.entries()).map(([localId, data]) => ({
        localUnionId: localId,
        localUnionName: data.localUnionName,
        tradeIds: data.tradeIds,
        unionType: data.unionType as 'union' | 'non-union' | 'mixed'
      }));

      setFormData({
        name: contractorData.name || '',
        status: contractorData.status?.toLowerCase() === 'active' ? 'active' : 'inactive',
        address: headOffice?.address || '',
        city: headOffice?.city || '',
        province: headOffice?.province?.id || '',
        postalCode: headOffice?.postalCode || '',
        coordinates: headOffice?.coordinates || [],
        subOffices: subOffices.map((office: any) => ({
          address: office.address || '',
          city: office.city || '',
          province: office.province?.id || '',
          postalCode: office.postalCode || '',
          coordinates: office.coordinates || []
        })),
        localTradeSelections
      });

      setEditingContractorId(contractorId);
      setShowContractorModal(true);
    } catch (error) {
      console.error('Error loading contractor data:', error);
      toast.error('Failed to load contractor data');
    }
  };

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear related errors when user makes changes
    if (formErrors[name as keyof MechanicalContractorFormErrors]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle sub office changes
  const handleSubOfficeChange = (index: number, field: string, value: string) => {
    const updatedSubOffices = [...formData.subOffices];
    updatedSubOffices[index] = {
      ...updatedSubOffices[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      subOffices: updatedSubOffices
    }));
  };

  // Handle modal back button
  const handleBack = () => {
    setCurrentModalStep(prev => prev - 1);
  };

  // Handle modal next button
  const handleNext = () => {
    if (currentModalStep === 1) {
      const isValid = validateStep1(formData, setFormErrors);
      if (isValid) {
        setCurrentModalStep(2);
      }
    } else if (currentModalStep === 2) {
      // Create a temporary errors object to capture validation messages
      const tempErrors: MechanicalContractorFormErrors = { ...initialMechanicalContractorFormErrors };
      const isValid = validateStep2(formData, (newErrors) => {
        Object.assign(tempErrors, newErrors);
        setFormErrors(newErrors);
      }, provinceOptions);

      if (isValid) {
        // Submit the form
        setShowSaveConfirmDialog(true);
      } else {
        // Show specific validation error in toast
        if (tempErrors.address) {
          toast.error(tempErrors.address);
        } else if (tempErrors.postalCode) {
          toast.error(tempErrors.postalCode);
        } else {
          toast.error('Please fix the validation errors before saving');
        }
      }
    }
  };

  // Confirm save
  const confirmSave = async () => {
    setIsSubmitting(true);
    setShowSaveConfirmDialog(false);

    try {
      // Update coordinates for main address before saving (only if all required fields are present)
      if (formData.address && formData.city && formData.province && formData.postalCode) {
        const mainCoordinates = await getCoordinatesFromAddress(
          formData.address,
          formData.city,
          formData.province,
          formData.postalCode
        );
        if (mainCoordinates) {
          formData.coordinates = mainCoordinates;
        }
      }

      // Update coordinates for sub offices before saving
      for (let i = 0; i < formData.subOffices.length; i++) {
        const office = formData.subOffices[i];
        if (office.address && office.city && office.province && office.postalCode) {
          const coordinates = await getCoordinatesFromAddress(
            office.address,
            office.city,
            office.province,
            office.postalCode
          );
          if (coordinates) {
            formData.subOffices[i].coordinates = coordinates;
          }
        }
      }

      // Format data for API
      const contractorTrades = formData.localTradeSelections.flatMap((selection: any) => {
        // If no trades selected for this local, create one entry with just the local and union type
        if (selection.tradeIds.length === 0) {
          return [{
            localUnionId: selection.localUnionId,
            unionType: selection.unionType
          }];
        }

        // Create one entry for each trade under this local
        return selection.tradeIds.map((tradeId: string) => ({
          trade: tradeId,
          localUnionId: selection.localUnionId,
          unionType: selection.unionType
        }));
      });

      // Format addresses - only include if data is provided
      const addresses = [];

      // Include head office only if at least one field is provided
      if (formData.address || formData.city || formData.province || formData.postalCode) {
        addresses.push({
          addressType: 'head-office',
          address: formData.address || null,
          city: formData.city || null,
          province: formData.province || null,
          postalCode: formData.postalCode || null,
          coordinates: (formData.coordinates && Array.isArray(formData.coordinates) && formData.coordinates.length > 0) ? formData.coordinates : null
        });
      }

      // Add valid sub offices
      const validSubOffices = formData.subOffices.filter(office =>
        office.address || office.city || office.province || office.postalCode
      );

      addresses.push(...validSubOffices.map(office => ({
        addressType: 'sub-office',
        address: office.address || null,
        city: office.city || null,
        province: office.province || null,
        postalCode: office.postalCode || null,
        coordinates: (office.coordinates && Array.isArray(office.coordinates) && office.coordinates.length > 0) ? office.coordinates : null
      })));

      const apiData: any = {
        name: formData.name,
        status: formData.status,
        contractorTrades: contractorTrades,
        address: addresses
      };

      // If we've already shown the warning, add the flag to bypass it
      if (hasShownSimilarNameWarning) {
        apiData.ignoreSimilarNameWarning = true;
      }


      // Update or Create mechanical contractor
      const savedContractor: any = editingContractorId ? await ContractorService.updateContractor(editingContractorId, apiData) : await ContractorService.createContractor(apiData);
      if(savedContractor.data.id){
        toast.success(editingContractorId ? 'Mechanical contractor updated successfully' : 'Mechanical contractor created successfully');
      } else {
        toast.error(editingContractorId ?'Failed to update mechanical contractor' : 'Failed to create mechanical contractor');
        return;
      }
      const updatedContractorResponse: any = await ContractorService.getContractorById(savedContractor.data.id);
      const updatedContractor = updatedContractorResponse.data;

      // Update the contractors list in parent (adds new or updates existing)
      onContractorUpdate(updatedContractor);

      if (editingContractorId) {
        // If editing, we need to clean up selected contractors to remove stale Locals and Trades
        // that may no longer exist after the edit
        let removedLocalsCount = 0;
        let removedTradesCount = 0;

        const updatedSelected = selectedContractors.map(sc => {
          if (sc.contractorId !== updatedContractor.id) return sc;

          // Get the new valid Local IDs from the updated contractor
          const validLocalIds = new Set(
            updatedContractor.contractorTrades
              ?.filter((ct: any) => ct.localUnion)
              .map((ct: any) => ct.localUnion!.id) || []
          );

          // Count removed locals
          const originalLocalsCount = sc.locals.length;

          // Filter out locals that no longer exist
          const cleanedLocals = sc.locals
            .filter(local => {
              const isValid = validLocalIds.has(local.localUnionId);
              if (!isValid) removedLocalsCount++;
              return isValid;
            })
            .map(local => {
              // For each local, also clean up trades that no longer exist
              const validTradeIdsForLocal = new Set(
                updatedContractor.contractorTrades
                  ?.filter((ct: any) => ct.localUnion?.id === local.localUnionId && ct.trade)
                  .map((ct: any) => ct.trade!.id) || []
              );

              const originalTradesCount = local.trades.length;
              const cleanedTrades = local.trades.filter(trade => {
                const isValid = validTradeIdsForLocal.has(trade.tradeId);
                if (!isValid) removedTradesCount++;
                return isValid;
              });

              return {
                ...local,
                trades: cleanedTrades
              };
            });

          return {
            ...sc,
            contractorName: updatedContractor.name,
            locals: cleanedLocals
          };
        });
        onChange(updatedSelected);

        // Notify user if any selections were removed
        if (removedLocalsCount > 0 || removedTradesCount > 0) {
          const messages = [];
          if (removedLocalsCount > 0) {
            messages.push(`${removedLocalsCount} Local${removedLocalsCount > 1 ? 's' : ''}`);
          }
          if (removedTradesCount > 0) {
            messages.push(`${removedTradesCount} Trade${removedTradesCount > 1 ? 's' : ''}`);
          }
          toast.info(
            `Contractor updated. Previously selected ${messages.join(' and ')} removed as they are no longer associated with this contractor.`,
            { autoClose: 5000 }
          );
        }
      } else {
        // If creating a new contractor, automatically add it to the selection (without locals/trades)
        const newContractorSelection: ContractorLocalTradeSelection = {
          contractorId: updatedContractor.id,
          contractorName: updatedContractor.name,
          locals: [] // Empty - user will manually select locals and trades
        };

        // Add the new contractor to the selection
        onChange([...selectedContractors, newContractorSelection]);
      }

      // Close dialogs and reset
      setShowSaveConfirmDialog(false);
      setShowContractorModal(false);
      setEditingContractorId(null);
      resetFormData();
    } catch (error: any) {
      // Get the error message from either error.message or error.response.data.message
      const errorMessage = error.message || error.response?.data?.message || '';

      // Check if this is a similar name warning (not a hard error)
      if (errorMessage.includes('similar name already exists') && !hasShownSimilarNameWarning) {
        const existingName = errorMessage.match(/"([^"]+)"/)?.[1] || 'another contractor';

        // Mark that we've shown the warning
        setHasShownSimilarNameWarning(true);

        // Show warning toast and do NOT proceed
        toast.warning(
          `A contractor with a similar name already exists: "${existingName}". Click "Add Mechanical Contractor" again to proceed.`,
          { autoClose: 5000 }
        );

        // Re-open the save confirmation dialog so user can click again
        setShowSaveConfirmDialog(true);
      } else {
        // Regular error - show error toast
        // Transform technical error messages to user-friendly ones
        let displayMessage = 'Error saving mechanical contractor';

        if (errorMessage) {
          if (errorMessage.includes('coordinates') && errorMessage.includes('should not be empty')) {
            displayMessage = 'Please provide complete address information including street, city, province, and postal code to save the contractor.';
          } else if (errorMessage.includes('coordinates')) {
            displayMessage = 'Invalid address information. Please check that all address fields are filled correctly.';
          } else {
            displayMessage = errorMessage;
          }
        }

        toast.error(displayMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cancel save
  const handleCancelSave = () => {
    setShowSaveConfirmDialog(false);
  };

  // Handle closing modal
  const handleCloseModal = () => {
    // Check if form has changes
    const hasChanges = formData.name !== initialMechanicalContractorFormData.name ||
      formData.status !== initialMechanicalContractorFormData.status ||
      formData.address !== initialMechanicalContractorFormData.address ||
      formData.city !== initialMechanicalContractorFormData.city ||
      formData.province !== initialMechanicalContractorFormData.province ||
      formData.postalCode !== initialMechanicalContractorFormData.postalCode ||
      formData.subOffices.length !== initialMechanicalContractorFormData.subOffices.length ||
      formData.localTradeSelections.length !== initialMechanicalContractorFormData.localTradeSelections.length;

    if (!hasChanges) {
      setShowContractorModal(false);
      setEditingContractorId(null);
      resetFormData();
      return;
    }

    // Show confirmation dialog
    setShowCloseConfirmDialog(true);
    setPendingCloseAction(() => () => {
      setShowContractorModal(false);
      setEditingContractorId(null);
      resetFormData();
      setShowCloseConfirmDialog(false);
    });
  };

  return (
    <div className="space-y-6">
      {/* Mechanical Contractor Multi-Select */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Select Mechanical Contractors
          </label>
          <button
            type="button"
            onClick={handleAddContractor}
            className="btn-secondary text-sm"
          >
            + Add Mechanical Contractor
          </button>
        </div>
        <MultiSelectDropdownInline
          options={contractorOptions}
          selectedValues={selectedContractorIds}
          onSelect={handleContractorSelectionChange}
          getValue={(option) => option.value}
          getDisplayText={(option) => option.label}
          placeholder="Select contractors"
        />
        {errors.contractors && (
          <p className="mt-1 text-sm text-red-600">{errors.contractors}</p>
        )}
      </div>

      {/* For each selected contractor */}
      {selectedContractors.map((contractor, contractorIndex) => {
        const availableLocals = getAvailableLocals(contractor.contractorId);
        const selectedLocalIds = contractor.locals.map(l => l.localUnionId);

        return (
          <div key={contractor.contractorId} className="border rounded-lg p-4 relative">
            {/* Remove Contractor Button */}
            <button
              onClick={() => handleRemoveContractor(contractor.contractorId)}
              className="absolute top-2 right-2 p-1 hover:bg-gray-100 rounded-full transition-colors"
              title="Remove contractor"
            >
              <X className="w-5 h-5 text-gray-500 hover:text-red-600" />
            </button>

            {/* Contractor Header */}
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-gray-500 uppercase">MECHANICAL CONTRACTOR</h4>
              <div className="flex items-center gap-2">
                <p className="text-base font-bold">{contractor.contractorName}</p>
                <button
                  type="button"
                  onClick={() => handleEditContractor(contractor.contractorId)}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                  title="Edit contractor"
                >
                  <Pencil className="w-4 h-4 text-gray-600 hover:text-[#A42823]" />
                </button>
              </div>
            </div>

            {/* Locals Multi-Select */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Locals
              </label>
              <MultiSelectDropdownInline
                options={availableLocals}
                selectedValues={selectedLocalIds}
                onSelect={(selectedIds) => handleLocalSelectionChange(contractor.contractorId, selectedIds)}
                getValue={(option) => option.value}
                getDisplayText={(option) => option.label}
                placeholder="Select locals"
              />
            </div>

            {/* For each selected local */}
            {contractor.locals.map((local, localIndex) => {
              const availableTrades = getAvailableTrades(contractor.contractorId, local.localUnionId);
              const selectedTradeIds = local.trades.map(t => t.tradeId);

              return (
                <div key={local.localUnionId} className="border border-gray-300 rounded-lg p-4 mb-4 relative">
                  {/* Remove Local Button */}
                  <button
                    onClick={() => handleRemoveLocal(contractor.contractorId, local.localUnionId)}
                    className="absolute top-2 right-2 p-1 hover:bg-gray-100 rounded-full transition-colors"
                    title="Remove local"
                  >
                    <X className="w-4 h-4 text-gray-500 hover:text-red-600" />
                  </button>

                  {/* Local Header */}
                  <div className="mb-4">
                    <p className="text-base font-bold">{local.localUnionName}</p>
                  </div>

                  {/* Trade Multi-Select */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Trades for {local.localUnionName}
                    </label>
                    <MultiSelectDropdownInline
                      options={availableTrades}
                      selectedValues={selectedTradeIds}
                      onSelect={(selectedIds) => handleTradeSelectionChange(contractor.contractorId, local.localUnionId, selectedIds)}
                      getValue={(option) => option.value}
                      getDisplayText={(option) => option.label}
                      placeholder="Select trades"
                    />
                  </div>

                  {/* For each selected trade */}
                  {local.trades.map((trade, tradeIndex) => (
                    <div key={trade.tradeId} className="border border-gray-300 rounded-lg p-4 mb-3 relative">
                      {/* Remove Trade Button */}
                      <button
                        onClick={() => handleRemoveTrade(contractor.contractorId, local.localUnionId, trade.tradeId)}
                        className="absolute top-2 right-2 p-1 hover:bg-gray-100 rounded-full transition-colors"
                        title="Remove trade"
                      >
                        <X className="w-4 h-4 text-gray-500 hover:text-red-600" />
                      </button>

                      <div className="mb-3">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase">TRADE</h4>
                        <p className="text-base font-bold">{trade.tradeName}</p>
                      </div>

                      {/* Journeymen, Apprentices, and Signatory Inputs */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Number of Journeymen
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={trade.journeymen === 0 ? '' : trade.journeymen}
                            onChange={(e) => handleTradeFieldChange(
                              contractor.contractorId,
                              local.localUnionId,
                              trade.tradeId,
                              'journeymen',
                              e.target.value
                            )}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Number of Apprentices
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={trade.apprentices === 0 ? '' : trade.apprentices}
                            onChange={(e) => handleTradeFieldChange(
                              contractor.contractorId,
                              local.localUnionId,
                              trade.tradeId,
                              'apprentices',
                              e.target.value
                            )}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Union Status
                          </label>
                          <SelectDropdown
                            options={[
                              { value: 'union', label: 'Union', group: '' },
                              { value: 'clac', label: 'CLAC', group: 'Non-union' },
                              { value: 'merit', label: 'Merit', group: 'Non-union' },
                              { value: 'other', label: 'Unrepresented', group: 'Non-union' }
                            ]}
                            selectedValue={trade.unionType}
                            onSelect={(value) => handleTradeFieldChange(
                              contractor.contractorId,
                              local.localUnionId,
                              trade.tradeId,
                              'unionType',
                              value
                            )}
                            getValue={(option) => option.value}
                            getDisplayText={(option) => option.label}
                            getGroup={(option) => option.group}
                            placeholder="Select"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        );
      })}

      {/* Mechanical Contractor Modal */}
      <MechanicalContractorDialogs
        showAddMechanicalContractorDialog={showContractorModal}
        showSaveConfirmDialog={showSaveConfirmDialog}
        showCloseConfirmDialog={showCloseConfirmDialog}
        isSubmitting={isSubmitting}
        formData={formData}
        errors={formErrors}
        currentStep={currentModalStep}
        editFormData={editingContractorId ? { id: editingContractorId } : null}
        addMechanicalContractorDialogContentRef={addMechanicalContractorDialogContentRef}
        provinceOptions={provinceOptions}
        isLoadingProvinces={isLoadingProvinces}
        handleCloseAddMechanicalContractorDialog={handleCloseModal}
        handleInputChange={handleInputChange}
        handleSubOfficeChange={handleSubOfficeChange}
        handleBack={handleBack}
        handleNext={handleNext}
        handleCancelSave={handleCancelSave}
        confirmSave={confirmSave}
        setShowCloseConfirmDialog={setShowCloseConfirmDialog}
        pendingCloseAction={pendingCloseAction}
        setFormData={setFormData}
        hideStatusToggle={true}
      />
    </div>
  );
};

export default MultiContractorTradesStep;
